// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — New Donors
// Path: src/components/dashboard/pollen/PollenNewDonors.jsx
//
// "They've arrived. Go welcome them."
// Surfaces donors whose first gift was in the last 90 days.
// First impressions matter — a personal welcome deepens
// the relationship before it fades.
//
// Optimized: two targeted queries instead of full-table fetch,
// avoiding the Supabase 1000-row default limit.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { UserPlus, X, Info, Sprout } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const PollenNewDonors = ({ onClose }) => {
  const { palette, navigate, dataVersion, members, cultivationQueue } = useApp();
  const [newDonors, setNewDonors] = useState([]);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        const cutoffStr = cutoff.toISOString().split("T")[0];

        // Paginated queries — Supabase caps at 1000 rows per request
        // Query A: Gifts in the last 90 days
        const recentGifts = [];
        let recentOffset = 0;
        while (true) {
          const { data: page, error } = await supabase
            .from("gifts")
            .select("member_id, gift_date, amount")
            .gte("gift_date", cutoffStr)
            .order("gift_date", { ascending: true })
            .range(recentOffset, recentOffset + PAGE_SIZE - 1);

          if (error || !page) break;
          recentGifts.push(...page);
          if (page.length < PAGE_SIZE) break;
          recentOffset += PAGE_SIZE;
        }

        // Query B: Paginate through ALL gifts before cutoff to build complete
        // exclusion set. With 6+ years of data, a single query may truncate.
        const hasOlderGift = new Set();
        let olderOffset = 0;
        const PAGE_SIZE = 1000;
        let pageCount = 0;
        while (true) {
          const { data: page, error: errPage } = await supabase
            .from("gifts")
            .select("member_id")
            .lt("gift_date", cutoffStr)
            .range(olderOffset, olderOffset + PAGE_SIZE - 1);

          pageCount++;
          console.log(`[PollenNewDonors] Page ${pageCount}: ${page?.length ?? 0} rows, error:`, errPage);
          if (errPage || !page) break;
          page.forEach((gift) => hasOlderGift.add(gift.member_id));
          if (page.length < PAGE_SIZE) break; // last page
          olderOffset += PAGE_SIZE;
        }
        console.log(`[PollenNewDonors] Exclusion set: ${hasOlderGift.size} unique member_ids from ${pageCount} pages`);

        // New donors = recent gift AND no older gift
        const candidateIds = [...new Set(recentGifts.map((g) => g.member_id))];
        console.log(`[PollenNewDonors] Candidates (recent gifts): ${candidateIds.length}`);
        console.log(`[PollenNewDonors] Candidates NOT in exclusion set:`, candidateIds.filter((id) => !hasOlderGift.has(id)).length);
        const firstGiftByMember = {};
        recentGifts.forEach((gift) => {
          const memberId = gift.member_id;
          if (hasOlderGift.has(memberId)) return;

          if (!firstGiftByMember[memberId]) {
            firstGiftByMember[memberId] = {
              memberId,
              firstGiftDate: gift.gift_date,
              firstGiftAmount: gift.amount,
              totalGifts: 0,
              totalAmount: 0,
            };
          }
          firstGiftByMember[memberId].totalGifts += 1;
          firstGiftByMember[memberId].totalAmount += Number(gift.amount) || 0;
        });

        const results = Object.values(firstGiftByMember)
          .sort((a, b) => b.firstGiftDate.localeCompare(a.firstGiftDate))
          .slice(0, 8);

        setNewDonors(results);
      } catch (err) {
        // silent
      }
    };
    load();
  }, [dataVersion]);

  const getDonorName = (donor) => {
    const member = members.find((m) => m.id === donor.memberId);
    if (!member) return "—";
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "—"
    );
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <UserPlus size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            New Donors
          </span>
          {newDonors.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {newDonors.length}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setShowInfo((prev) => !prev)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
              opacity: showInfo ? 1 : 0.5,
              transition: "opacity 0.15s",
            }}
            title="How this card works"
          >
            <Info size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {showInfo && (
        <div
          style={{
            padding: "10px 16px",
            fontSize: FONT_SIZES.xs,
            lineHeight: 1.5,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            background: `${palette.accent1}06`,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          This card shows donors whose <strong>first-ever gift</strong> was
          within the last 90 days. A personal welcome early in the relationship
          dramatically increases the chance of a second gift.
        </div>
      )}

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 4px",
          fontSize: "0.65rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          fontStyle: "italic",
        }}
      >
        Go welcome them. First gift in the last 90 days.
      </div>

      {/* Donor list */}
      {newDonors.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No new donors in the last 90 days.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", padding: "4px 0" }}>
          {newDonors.map((donor) => (
            <div
              key={donor.memberId}
              onClick={() => {
                const fullMember = members.find((m) => m.id === donor.memberId);
                if (fullMember) navigate("memberDetail", fullMember);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 16px",
                cursor: members.find((m) => m.id === donor.memberId) ? "pointer" : "default",
                transition: "background 0.1s",
              }}
              onMouseEnter={(event) => {
                if (donor.members) event.currentTarget.style.background = `${palette.accent1}08`;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getDonorName(donor)}
                </div>
              </div>
              {cultivationQueue.some((q) => q.memberId === donor.memberId) && (
                <Sprout size={ICON_SIZES.xs} style={{ color: "#10b981", flexShrink: 0 }} title="In cultivation queue" />
              )}
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: "#3b82f6",
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {fmtCurrency(donor.firstGiftAmount)}
              </span>
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {fmtDate(donor.firstGiftDate)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
